import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Button from 'shared/components/UC/Button';
import Toast from 'shared/components/Toast';
import authService from '../services/authService';
import { AuthError } from '../services/authService';
import styles from './ResetPassword.module.css';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'error' | 'success' | 'warning' | 'info';
  } | null>(null);

  useEffect(() => {
    const validateToken = async () => {
      if (!token || !email) {
        setValidatingToken(false);
        setTokenValid(false);
        return;
      }

      try {
        await authService.validateResetToken(email, token);
        setTokenValid(true);
      } catch (error) {
        setTokenValid(false);
        if (error instanceof AuthError) {
          setToast({
            message: error.message,
            type: 'error',
          });
        }
      } finally {
        setValidatingToken(false);
      }
    };

    validateToken();
  }, [token, email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token || !email) {
      setToast({
        message: 'Código o email faltante. Verifica el enlace.',
        type: 'error',
      });
      return;
    }

    if (password.length < 6) {
      setToast({
        message: 'La contraseña debe tener al menos 6 caracteres',
        type: 'warning',
      });
      return;
    }

    if (password !== confirmPassword) {
      setToast({
        message: 'Las contraseñas no coinciden',
        type: 'warning',
      });
      return;
    }

    try {
      setLoading(true);
      await authService.resetPassword(email, token, password);

      setToast({
        message:
          'Contraseña restablecida exitosamente. Serás redirigido al login.',
        type: 'success',
      });

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/admin', { replace: true });
      }, 2000);
    } catch (error) {
      if (error instanceof AuthError) {
        setToast({
          message: error.message,
          type: 'error',
        });
      } else {
        setToast({
          message: 'Error al restablecer la contraseña',
          type: 'error',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRequestNewReset = async () => {
    if (!email) {
      setToast({
        message: 'Email no disponible para solicitar nuevo enlace',
        type: 'error',
      });
      return;
    }

    try {
      setLoading(true);
      await authService.requestPasswordReset(email);

      setToast({
        message: 'Se ha enviado un nuevo enlace de recuperación a tu correo.',
        type: 'success',
      });
    } catch (error) {
      setToast({
        message: 'Error al solicitar nuevo enlace',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  if (validatingToken) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.loading}>
            <div className={styles.spinner}></div>
            <p>Validando enlace de recuperación...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!token || !email || !tokenValid) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.header}>
            <h1>Enlace Inválido</h1>
            <p>El enlace de recuperación no es válido o ha expirado</p>
          </div>
          <div className={styles.content}>
            <p className={styles.helpText}>
              {!token || !email
                ? 'Código o email faltante en la URL. Verifica el enlace recibido por email.'
                : 'El enlace de recuperación ha expirado o ya fue utilizado.'}
            </p>
            <div className={styles.actions}>
              <Button variant="secondary" onClick={() => navigate('/admin')}>
                Ir al Login
              </Button>
              {email && (
                <Button
                  variant="primary"
                  onClick={handleRequestNewReset}
                  disabled={loading}
                >
                  {loading ? 'Enviando...' : 'Solicitar Nuevo Enlace'}
                </Button>
              )}
            </div>
          </div>
        </div>

        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <h1>Restablecer Contraseña</h1>
          <p>Configura tu nueva contraseña</p>
          <div className={styles.userInfo}>
            <strong>Email:</strong> {email}
          </div>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label htmlFor="password" className={styles.label}>
              Nueva Contraseña *
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={styles.input}
              placeholder="Mínimo 6 caracteres"
              required
              disabled={loading}
              minLength={6}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="confirmPassword" className={styles.label}>
              Confirmar Contraseña *
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={styles.input}
              placeholder="Repite la contraseña"
              required
              disabled={loading}
              minLength={6}
            />
          </div>

          <div className={styles.requirements}>
            <h4>Requisitos de la contraseña:</h4>
            <ul>
              <li className={password.length >= 6 ? styles.valid : ''}>
                Al menos 6 caracteres
              </li>
              <li
                className={
                  password === confirmPassword && password ? styles.valid : ''
                }
              >
                Las contraseñas deben coincidir
              </li>
            </ul>
          </div>

          <div className={styles.actions}>
            <Button
              type="submit"
              variant="primary"
              disabled={
                loading || password.length < 6 || password !== confirmPassword
              }
              className={styles.submitButton}
            >
              {loading ? 'Restableciendo...' : 'Restablecer Contraseña'}
            </Button>
          </div>
        </form>

        <div className={styles.helpSection}>
          <p className={styles.helpText}>
            ¿Problemas con el enlace?{' '}
            <button
              type="button"
              onClick={handleRequestNewReset}
              disabled={loading}
              className={styles.linkButton}
            >
              Solicitar nuevo enlace
            </button>
          </p>
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default ResetPassword;
